import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTournaments } from '../../../shared/hooks/useTournaments';
import { useFavorites } from '../../../shared/contexts/FavoritesContext';
import ScoreAppHeader from '../../../shared/components/layout/ScoreAppHeader';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';

interface ReportsStandingsPageProps {
  isMobile?: boolean;
  onToggleSidebar?: () => void;
}

const ReportsStandingsPage: React.FC<ReportsStandingsPageProps> = ({ isMobile = false, onToggleSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'all' | 'favourites'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const { tournaments, loading, error } = useTournaments();
  const { isTournamentFavorite } = useFavorites();

  // Extract organization ID from URL
  const orgIdFromPath = extractOrgIdFromPath(location.pathname);

  // Helper function to get country code for flag
  const getCountryCode = (country: string): string => {
    const countryMap: { [key: string]: string } = {
      'Germany': 'de',
      'Europe': 'eu',
      'India': 'in',
      'Asia': 'asia',
      'Spain': 'es',
      'France': 'fr',
      'United States': 'us',
      'England': 'gb',
      'Italy': 'it',
      'Other': 'un'
    };
    return countryMap[country] || 'un';
  };

  // Handle tournament click navigation
  const handleTournamentClick = (tournamentId: string) => {
    const actualOrgId = orgIdFromPath || 'default';
    navigate(`/o/${actualOrgId}/reports/standings/${tournamentId}`);
  };

  // Filter tournaments based on active tab and search query
  const filteredTournaments = useMemo(() => {
    let filtered = tournaments;

    // Filter by tab
    if (activeTab === 'favourites') {
      filtered = tournaments.filter(tournament => isTournamentFavorite(tournament.id));
    }

    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(tournament =>
        tournament.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  }, [tournaments, activeTab, searchQuery, isTournamentFavorite]);

  // Group tournaments by country
  const tournamentsByCountry = useMemo(() => {
    const grouped: { [key: string]: any[] } = {};
    
    filteredTournaments.forEach(tournament => {
      const country = tournament.country || 'Other';
      if (!grouped[country]) {
        grouped[country] = [];
      }
      grouped[country].push(tournament);
    });

    return grouped;
  }, [filteredTournaments]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="text-gray-400 text-4xl mb-3">⏳</div>
          <p className="text-gray-600">Loading tournaments...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="text-red-400 text-4xl mb-3">❌</div>
          <p className="text-red-600">Error loading tournaments: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <ScoreAppHeader
        sticky={true}
        isMobile={isMobile}
        onToggleSidebar={onToggleSidebar}
        leftContent={
          <div className={`flex ${isMobile ? 'space-x-4' : 'space-x-8'}`}>
            <button
              onClick={() => setActiveTab('all')}
              className={`pb-2 px-1 border-b-2 font-medium ${isMobile ? 'text-xs' : 'text-sm'} min-h-44 ${
                activeTab === 'all'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setActiveTab('favourites')}
              className={`pb-2 px-1 border-b-2 font-medium ${isMobile ? 'text-xs' : 'text-sm'} min-h-44 ${
                activeTab === 'favourites'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Favourites
            </button>
          </div>
        }
        centerContent={
          <div className="flex-1 flex justify-center">
            <div className="relative w-full max-w-md">
              <div className={`absolute inset-y-0 left-0 ${isMobile ? 'pl-3' : 'pl-3'} flex items-center pointer-events-none`}>
                <svg className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'} text-gray-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder={isMobile ? "Search..." : "Search tournaments..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`block w-full ${isMobile ? 'pl-8 pr-3 py-2' : 'pl-10 pr-3 py-2'} border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isMobile ? 'text-sm' : 'sm:text-sm'}`}
              />
            </div>
          </div>
        }
        rightContent={
          <div className={`flex items-center ${isMobile ? 'space-x-2' : 'space-x-3'}`}>
            {/* Empty right content for now */}
          </div>
        }
      />

      {/* Content */}
      <div className="p-3 sm:p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm text-gray-600">Loading tournaments...</span>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-semibold text-gray-700 mb-2">Error Loading Tournaments</h2>
            <p className="text-gray-500">Failed to load tournaments. Please try again later.</p>
          </div>
        ) : Object.keys(tournamentsByCountry).length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">🏆</div>
            <h2 className="text-2xl font-semibold text-gray-700 mb-2">
              {activeTab === 'favourites' ? 'No Favorite Tournaments' : 'No Tournaments Found'}
            </h2>
            <p className="text-gray-500">
              {activeTab === 'favourites' 
                ? 'You haven\'t favorited any tournaments yet.' 
                : searchQuery 
                  ? 'Try adjusting your search or check back later.'
                  : 'No tournaments available at the moment.'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(tournamentsByCountry).map(([country, countryTournaments]) => (
              <div key={country} className="bg-white rounded-lg shadow-sm border border-gray-200">
                {/* Country Header */}
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <img 
                      src={`https://flagcdn.com/w20/${getCountryCode(country)}.png`}
                      alt={`${country} flag`}
                      className="w-5 h-4 rounded object-cover"
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/20x16/cccccc/666666?text=🏳️';
                      }}
                    />
                    <h3 className="text-lg font-semibold text-gray-900">{country}</h3>
                    <span className="text-sm text-gray-500">({countryTournaments.length} tournaments)</span>
                  </div>
                </div>

                {/* Tournaments List */}
                <div className="divide-y divide-gray-200">
                  {countryTournaments.map((tournament) => (
                    <button
                      key={tournament.id}
                      onClick={() => handleTournamentClick(tournament.id)}
                      className="w-full px-6 py-4 hover:bg-gray-50 transition-colors text-left"
                    >
                      <div className="flex items-center space-x-4">
                        <img 
                          src={tournament.logo || 'https://via.placeholder.com/32x32/cccccc/666666?text=⚽'} 
                          alt={`${tournament.name} logo`}
                          className="w-8 h-8 rounded object-cover"
                          onError={(e) => {
                            e.currentTarget.src = 'https://via.placeholder.com/32x32/cccccc/666666?text=⚽';
                          }}
                        />
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-gray-900">{tournament.name}</h4>
                          <p className="text-sm text-gray-500">{tournament.country}</p>
                        </div>
                        <div className="text-gray-400">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsStandingsPage;
